import {useMemo, useState} from 'react'
import type {IntlShape} from 'react-intl'
import {useMutation} from '@apollo/client'
import {Button, Form, InputGroup} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  BEGIN_PASSKEY_REGISTRATION,
  COMPLETE_PASSKEY_REGISTRATION,
  DELETE_PASSKEY,
} from '../../gql/mutations/authMutations'
import {DELETE_USER_PASSKEY} from '../../gql/mutations/userMutations'
import {
  IPasskeyCredential,
  IResBeginPasskeyRegistration,
  IResCompletePasskeyRegistration,
  IResDeletePasskey,
} from '../../types/auth'
import {
  createPasskey,
  getPasskeyErrorMessage,
  getPasskeyUnavailableMessage,
  isPasskeySupported,
} from '../../helpers/passkeys'
import {FormattedMessage, useIntl} from 'react-intl'
import {SECURITY_MESSAGES as S} from './securityMessages'

interface PasskeySettingsPanelProps {
  credentials?: IPasskeyCredential[]
  canManageOwnPasskeys?: boolean
  canManageUserPasskeys?: boolean
  targetUserId?: string | number | null
  onUpdated?: () => void | Promise<unknown>
}

type DeleteUserPasskeyResponse = {
  deleteUserPasskeyMutation: {
    success: boolean
    errors?: {field?: string | null; message: string}[] | null
  }
}

const makeFormatDate = (intl: IntlShape) => (value?: string | null) => {
  if (!value) return intl.formatMessage(S.passkeyNeverUsed)

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

export const PasskeySettingsPanel = ({
  credentials = [],
  canManageOwnPasskeys = false,
  canManageUserPasskeys = false,
  targetUserId,
  onUpdated,
}: PasskeySettingsPanelProps) => {
  const intl = useIntl()
  const formatDate = makeFormatDate(intl)
  // Un administrador gestionando las passkeys de OTRO usuario: cambia el verbo
  // (revocar en vez de quitar) y varios textos de la pantalla.
  const isAdminManagingOther = Boolean(canManageUserPasskeys && !canManageOwnPasskeys)
  const [passkeyName, setPasskeyName] = useState('')
  const [gqlBeginPasskeyRegistration, {loading: loadingBeginRegistration}] =
    useMutation<IResBeginPasskeyRegistration>(BEGIN_PASSKEY_REGISTRATION)
  const [gqlCompletePasskeyRegistration, {loading: loadingCompleteRegistration}] =
    useMutation<IResCompletePasskeyRegistration>(COMPLETE_PASSKEY_REGISTRATION)
  const [gqlDeletePasskey, {loading: loadingDeletePasskey}] =
    useMutation<IResDeletePasskey>(DELETE_PASSKEY)
  const [gqlDeleteUserPasskey, {loading: loadingDeleteUserPasskey}] =
    useMutation<DeleteUserPasskeyResponse>(DELETE_USER_PASSKEY)

  const passkeySupported = useMemo(() => isPasskeySupported(), [])
  const canRegisterPasskeys = canManageOwnPasskeys
  const canRevokePasskeys = canManageOwnPasskeys || canManageUserPasskeys
  const loading =
    loadingBeginRegistration ||
    loadingCompleteRegistration ||
    loadingDeletePasskey ||
    loadingDeleteUserPasskey

  const runUpdated = async () => {
    if (onUpdated) {
      await Promise.resolve(onUpdated())
    }
  }

  const handleCreatePasskey = async () => {
    if (!canRegisterPasskeys) {
      toast.error(intl.formatMessage(S.toastOnlyFromProfile))
      return
    }

    if (!passkeySupported) {
      toast.info(getPasskeyUnavailableMessage(intl))
      return
    }

    try {
      const beginResponse = await gqlBeginPasskeyRegistration({
        variables: {
          name: passkeyName.trim() || null,
        },
      })

      const result = beginResponse.data?.beginPasskeyRegistration
      if (!result?.success || !result.challengeId || !result.publicKeyOptions) {
        toast.error(result?.message || intl.formatMessage(S.toastCannotStart))
        return
      }

      const credential = await createPasskey(result.publicKeyOptions)
      const completeResponse = await gqlCompletePasskeyRegistration({
        variables: {
          challengeId: result.challengeId,
          credential,
        },
      })

      const completeResult = completeResponse.data?.completePasskeyRegistration
      if (!completeResult?.success) {
        toast.error(completeResult?.message || intl.formatMessage(S.toastCannotFinish))
        return
      }

      toast.success(completeResult.message || intl.formatMessage(S.toastRegistered))
      setPasskeyName('')
      await runUpdated()
    } catch (error) {
      toast.error(getPasskeyErrorMessage(intl, error, intl.formatMessage(S.toastCannotCreate)))
    }
  }

  const handleDeletePasskey = async (passkey: IPasskeyCredential) => {
    if (!canRevokePasskeys) {
      toast.error(intl.formatMessage(S.toastNoPermission))
      return
    }

    // Frase completa por variante: el verbo y el complemento cambian juntos.
    const confirmed = window.confirm(
      intl.formatMessage(
        isAdminManagingOther ? S.passkeyConfirmRevoke : S.passkeyConfirmRemove,
        {name: passkey.name}
      )
    )
    if (!confirmed) return

    try {
      if (canManageUserPasskeys && !canManageOwnPasskeys) {
        if (!targetUserId) {
          toast.error(intl.formatMessage(S.toastUserNotFound))
          return
        }

        const response = await gqlDeleteUserPasskey({
          variables: {
            userId: targetUserId,
            passkeyId: passkey.id,
          },
        })

        const result = response.data?.deleteUserPasskeyMutation
        if (!result?.success) {
          toast.error(result?.errors?.[0]?.message || intl.formatMessage(S.toastCannotRevoke))
          return
        }

        toast.success(intl.formatMessage(S.toastRevoked))
        await runUpdated()
        return
      }

      const response = await gqlDeletePasskey({
        variables: {
          passkeyId: passkey.id,
        },
      })

      const result = response.data?.deletePasskey
      if (!result?.success) {
        toast.error(result?.message || intl.formatMessage(S.toastCannotRemove))
        return
      }

      toast.success(result.message || intl.formatMessage(S.toastRemoved))
      await runUpdated()
    } catch (error) {
      toast.error(
        getPasskeyErrorMessage(
          intl,
          error,
          intl.formatMessage(isAdminManagingOther ? S.toastCannotRevoke : S.toastCannotRemove)
        )
      )
    }
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 mb-6'>
        <div>
          <div className='fw-bold mb-2'>
            <FormattedMessage {...S.passkeysTitle} />
          </div>
          <div className='text-muted fs-6'>
            <FormattedMessage {...S.passkeysIntro} />
          </div>
        </div>
        <span className={`badge badge-light-${credentials.length > 0 ? 'success' : 'secondary'}`}>
          {credentials.length > 0
            ? intl.formatMessage(S.passkeysRegistered, {count: credentials.length})
            : intl.formatMessage(S.passkeysNone)}
        </span>
      </div>

      {canRegisterPasskeys && !passkeySupported && (
        <div className='alert alert-warning mb-6'>{getPasskeyUnavailableMessage(intl)}</div>
      )}

      {canRegisterPasskeys && (
        <div className='border rounded p-5 bg-light mb-6'>
          <div className='fw-semibold mb-3'>
            <FormattedMessage {...S.passkeysAddTitle} />
          </div>
          <InputGroup className='mb-3'>
            <Form.Control
              placeholder={intl.formatMessage(S.passkeysNamePlaceholder)}
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
              maxLength={120}
            />
            <Button
              type='button'
              className='btn btn-primary'
              onClick={handleCreatePasskey}
              disabled={loading || !passkeySupported}
            >
              {!loading && <FormattedMessage {...S.passkeysAddButton} />}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <FormattedMessage id='admin.oneUser.pleaseWait' defaultMessage='Please wait...' />
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </Button>
          </InputGroup>
          <div className='text-muted fs-7'>
            <FormattedMessage {...S.passkeysAddHint} />
          </div>
        </div>
      )}

      <div className='border rounded p-5 bg-light'>
        <div className='fw-semibold mb-4'>
          <FormattedMessage {...S.passkeysRegisteredTitle} />
        </div>

        {credentials.length === 0 && (
          <div className='text-muted fs-7'>
            {isAdminManagingOther ? (
              <FormattedMessage {...S.passkeysEmptyOther} />
            ) : (
              <FormattedMessage {...S.passkeysEmptyOwn} />
            )}
          </div>
        )}

        {credentials.map((passkey) => (
          <div
            key={passkey.id}
            className='d-flex justify-content-between align-items-start flex-wrap gap-3 border rounded bg-white p-4 mb-3'
          >
            <div>
              <div className='fw-bold text-gray-800'>
                {passkey.name || intl.formatMessage(S.passkeyUnnamed)}
              </div>
              <div className='text-muted fs-7'>
                {intl.formatMessage(S.passkeyCreated, {date: formatDate(passkey.created)})}
              </div>
              <div className='text-muted fs-7'>
                {intl.formatMessage(S.passkeyLastUsed, {date: formatDate(passkey.lastUsedAt)})}
              </div>
              {passkey.transports && passkey.transports.length > 0 && (
                <div className='text-muted fs-7'>
                  {intl.formatMessage(S.passkeyTransports, {list: passkey.transports.join(', ')})}
                </div>
              )}
            </div>

            <Button
              type='button'
              className='btn btn-light-danger'
              onClick={() => handleDeletePasskey(passkey)}
              disabled={loadingDeletePasskey || loadingDeleteUserPasskey}
            >
              {isAdminManagingOther ? (
                <FormattedMessage {...S.passkeyRevoke} />
              ) : (
                <FormattedMessage {...S.passkeyRemove} />
              )}
            </Button>
          </div>
        ))}
      </div>

      {canManageUserPasskeys && !canManageOwnPasskeys && (
        <div className='text-muted fs-7 mt-4'>
          <FormattedMessage {...S.passkeysAdminNote} />
        </div>
      )}
    </div>
  )
}
